import { TABLEAU20_COLORS } from "@/lib/utils/vega-chart-shared";

interface PieChartWidgetProps {
  title: string;
  data: Array<{ label: string; value: number }>;
  chartColors?: string[] | null;
}

export function PieChartWidget({ title, data, chartColors }: PieChartWidgetProps) {
  const colors = chartColors || TABLEAU20_COLORS;
  const total = data.reduce((sum, item) => sum + item.value, 0);
  if (total === 0) return null;

  let currentAngle = -90;
  const slices = data.map((item, index) => {
    const percentage = (item.value / total) * 100;
    const angle = (item.value / total) * 360;
    const startAngle = currentAngle;
    currentAngle += angle;

    const startRad = (startAngle * Math.PI) / 180;
    const endRad = ((startAngle + angle) * Math.PI) / 180;
    const x1 = 50 + 40 * Math.cos(startRad);
    const y1 = 50 + 40 * Math.sin(startRad);
    const x2 = 50 + 40 * Math.cos(endRad);
    const y2 = 50 + 40 * Math.sin(endRad);
    const largeArc = angle > 180 ? 1 : 0;

    const path =
      angle >= 360
        ? "M 50 10 A 40 40 0 1 1 49.99 10 Z"
        : `M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArc} 1 ${x2} ${y2} Z`;

    return { ...item, percentage, path, color: colors[index % colors.length] };
  });

  return (
    <div className="bg-muted/30 rounded-lg p-3">
      <p className="text-xs font-medium text-center mb-2">{title}</p>
      <div className="flex items-center gap-3">
        <svg
          viewBox="0 0 100 100"
          className="w-20 h-20 flex-shrink-0"
          role="img"
          aria-label={title}
        >
          <title>{title}</title>
          {slices.map((slice) => (
            <path key={slice.label} d={slice.path} fill={slice.color} />
          ))}
        </svg>
        <div className="flex-1 space-y-1 min-w-0">
          {slices.map((slice) => (
            <div key={slice.label} className="flex items-center gap-1.5 text-[10px]">
              <div
                className="w-2 h-2 rounded-sm flex-shrink-0"
                style={{ backgroundColor: slice.color }}
              />
              <span className="truncate flex-1">{slice.label}</span>
              <span className="text-muted-foreground flex-shrink-0">
                {slice.value} ({slice.percentage.toFixed(0)}%)
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
